import { normalizeSurroundingDetails } from './surrounding-config.js';

/**
 * Data store - keeps a fast in-memory cache and persists to IndexedDB.
 * localStorage is only used as a legacy migration fallback.
 *
 * Schema:
 *   locations: [
 *     { id, name, type, imageData,
 *       zones: [
 *         { id, name, patternSeed, imageData, rowImages: [],
 *           layout: { columns: 1, maxRows: 1, direction: 'top-bottom', columnLabels: [] },
 *           items: [
 *             { id, label, tags, imageData, tagImageData, ocrText,
 *               details: { brand, size, color, category, material, care, season },
 *               stack: 0,    // column index (0-based)
 *               position: 0, // slot from bottom (0 = bottom of stack)
 *               row: 0,      // depth row (0 = front, 1 = behind, 2 = deepest back)
 *               cropY: 50    // % down the image for spine strip crop (0-100)
 *             }
 *           ]
 *         }
 *       ]
 *     }
 *   ]
 */

const STORAGE_KEY = 'thecloset_v1';
const DB_NAME = 'thecloset_storage';
const DB_VERSION = 1;
const DB_STORE_NAME = 'app_state';
const LEGACY_DB_STATE_KEY = 'state';
const GUEST_DB_STATE_KEY = 'state:guest';

const DEFAULT_LAYOUT = { columns: 1, maxRows: 1, direction: 'top-bottom', columnLabels: [] };
const DEFAULT_VIEWS = ['front', 'quarter', 'side', 'back', 'move'];
const DEFAULT_POSE_SLOT = { imageData: '', mediaId: '', sha256: '', capturedAt: '', syncStatus: 'empty' };
const DEFAULT_POSE_SLOTS = Object.fromEntries(DEFAULT_VIEWS.map(view => [view, { ...DEFAULT_POSE_SLOT }]));
const DEFAULT_MIRROR = {
  subjectImageData: '',
  subjectMediaId: '',
  views: {},
  viewMediaIds: {},
  poseSlots: DEFAULT_POSE_SLOTS,
  activeView: 'front',
  activePoseId: 'front',
  outfits: [],
  ootdHistory: [],
};
const DEFAULT_CALENDAR_EVENT = {
  title: '',
  date: '',
  type: 'event',
  repeat: 'none',
  note: '',
  itemIds: [],
};
const DEFAULT_WITNESS_ENTRY = {
  note: '',
  reflection: '',
  summary: '',
  kind: 'compression',
  weather: '',
};
const DEFAULT_SURROUNDING_BOARD_POST = {
  branch: 'objects',
  body: '',
  prompt: '',
  linkedItemId: '',
  destination: 'self',
};
const DEFAULT_ITEM_DETAILS = {
  profileType: 'clothing',
  brand: '',
  size: '',
  color: '',
  category: '',
  material: '',
  care: '',
  season: '',
};
const DEFAULT_AI_MARKET = {
  currentMarketValue: '',
  aftermarketValue: '',
  conditionSummary: '',
  conditionRating: '',
  resellRating: '',
  recommendedPlatform: '',
  platformRationale: '',
  shoppingPoshProwessScore: '',
  shoppingPoshProwessValue: '',
  disclaimer: '',
  updatedAt: '',
  source: '',
};
const DEFAULT_ACTIVITY = {
  lastAdded: null,
  lastMoved: null,
  lastRemoved: null,
  auditLog: [],
};
const DEFAULT_INTENT = { wear: false, sell: false };
const DEFAULT_LISTING = { status: 'none', platform: '', listingUrl: '', price: '' };
const DEFAULT_WEAR_STATUS = 'clean';
const DEFAULT_COLLECTION_ID = 'collection_default';
const DEFAULT_COLLECTION = { id: DEFAULT_COLLECTION_ID, name: 'My Space', vibe: 'closet', createdAt: '' };
const DEFAULT_PREFERENCES = { weatherSource: '', weatherZip: '', defaultToSurroundings: false, defaultSurroundingBranch: 'library', surroundingsInterface: 'simple', surroundingAiAssist: false, sockPairsAvailable: 12, browserNotifications: false };
const CURRENT_SCHEMA_VERSION = 10;
const EMPTY_STATE = {
  schemaVersion: CURRENT_SCHEMA_VERSION,
  collections: [{ ...DEFAULT_COLLECTION }],
  activeCollectionId: DEFAULT_COLLECTION_ID,
  locations: [],
  mirror: { ...DEFAULT_MIRROR },
  calendar: [],
  witness: [],
  surroundingBoards: [],
  activity: { ...DEFAULT_ACTIVITY },
  preferences: { ...DEFAULT_PREFERENCES },
};

let lastMigrationInfo = { migrated: false, from: null, to: CURRENT_SCHEMA_VERSION };

let memoryState = clone(EMPTY_STATE);
let initPromise = null;
let dbPromise = null;
let undoSnapshot = null;
let undoLabel = '';

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function authenticatedUserId() {
  try {
    const user = JSON.parse(localStorage.getItem('mirror_auth_user') || 'null');
    return Number.isInteger(Number(user?.id)) && Number(user.id) > 0 ? Number(user.id) : null;
  } catch {
    return null;
  }
}

function profileStateKey(userId = authenticatedUserId()) {
  return userId ? `state:user:${userId}` : GUEST_DB_STATE_KEY;
}

function legacyFallbackKey(userId = authenticatedUserId()) {
  return userId ? `${STORAGE_KEY}:user:${userId}` : STORAGE_KEY;
}

function normalizePersistError(error) {
  if (error && (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED')) {
    return new Error('Browser storage is full. Photos are already resized/compressed, but this browser has a local quota. Export a backup zip, then use Compact Photos or remove older looks/large reference photos.');
  }
  return new Error(error && error.message ? error.message : 'Browser storage could not save your data.');
}

function emitPersistError(error) {
  if (typeof window === 'undefined' || !window.dispatchEvent || typeof CustomEvent === 'undefined') return;
  window.dispatchEvent(new CustomEvent('thecloset:persist-error', {
    detail: { message: normalizePersistError(error).message },
  }));
}

function emitAuditEntry(entry) {
  if (typeof window === 'undefined' || !window.dispatchEvent || typeof CustomEvent === 'undefined') return;
  window.dispatchEvent(new CustomEvent('thecloset:audit-entry', {
    detail: { entry: clone(entry) },
  }));
}

function emitStateChange(state) {
  if (typeof window === 'undefined' || !window.dispatchEvent || typeof CustomEvent === 'undefined') return;
  window.dispatchEvent(new CustomEvent('mirror:state-change', {
    detail: { state: clone(state) },
  }));
}

function openDatabase() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available in this browser.'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(DB_STORE_NAME)) db.createObjectStore(DB_STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Browser storage could not be opened.'));
  });

  return dbPromise;
}

async function readIndexedState(key = profileStateKey()) {
  try {
    const db = await openDatabase();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(DB_STORE_NAME, 'readonly');
      const request = tx.objectStore(DB_STORE_NAME).get(key);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error || new Error('Browser storage could not be read.'));
    });
  } catch {
    return null;
  }
}

async function writeIndexedState(state, key = profileStateKey()) {
  const db = await openDatabase();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE_NAME, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error('Browser storage could not be saved.'));
    tx.onabort = () => reject(tx.error || new Error('Browser storage could not be saved.'));
    tx.objectStore(DB_STORE_NAME).put(clone(state), key);
  });
}

async function deleteIndexedStatesMatching(predicate) {
  const db = await openDatabase();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE_NAME, 'readwrite');
    const store = tx.objectStore(DB_STORE_NAME);
    const request = store.openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return;
      if (predicate(String(cursor.key))) cursor.delete();
      cursor.continue();
    };
    request.onerror = () => reject(request.error || new Error('Browser storage could not be cleaned.'));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error('Browser storage could not be cleaned.'));
  });
}

function loadLegacyState(userId = authenticatedUserId()) {
  try {
    const raw = JSON.parse(localStorage.getItem(legacyFallbackKey(userId)));
    return raw ? migrateState(raw) : null;
  } catch {
    return null;
  }
}

function normalizeLayout(layout = {}) {
  const columns = Math.max(1, parseInt(layout.columns ?? DEFAULT_LAYOUT.columns, 10) || 1);
  const maxRows = Math.max(1, parseInt(layout.maxRows ?? DEFAULT_LAYOUT.maxRows, 10) || 1);
  const direction = layout.direction === 'front-back' ? 'front-back' : 'top-bottom';
  const labels = Array.isArray(layout.columnLabels) ? layout.columnLabels.slice(0, columns) : [];
  while (labels.length < columns) labels.push('');
  return { columns, maxRows, direction, columnLabels: labels };
}

function normalizeZonePositions(zone) {
  const groups = new Map();
  zone.items.forEach(item => {
    const key = `${item.stack ?? 0}:${item.row ?? 0}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  });

  groups.forEach(items => {
    items
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .forEach((item, idx) => {
        item.position = idx;
      });
  });
}

function nextTopPosition(zone, stack, row) {
  return zone.items.filter(i => (i.stack ?? 0) === stack && (i.row ?? 0) === row).length;
}

function normalizeRowImages(zone) {
  const maxRows = zone.layout?.maxRows || 1;
  const rowImages = Array.isArray(zone.rowImages) ? zone.rowImages.slice(0, maxRows) : [];
  while (rowImages.length < maxRows) rowImages.push('');
  zone.rowImages = rowImages.map(imageData => typeof imageData === 'string' ? imageData : '');
}

function normalizeTags(tags) {
  if (!Array.isArray(tags)) return [];
  const seen = new Set();
  return tags
    .map(tag => String(tag || '').trim())
    .filter(Boolean)
    .filter(tag => {
      const key = tag.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function normalizeItemDetails(details) {
  const safe = details && typeof details === 'object' ? details : {};
  return {
    profileType: ['clothing', 'accessory', 'hair_beauty', 'personal_care'].includes(safe.profileType) ? safe.profileType : 'clothing',
    brand: String(safe.brand || '').trim(),
    size: String(safe.size || '').trim(),
    color: String(safe.color || '').trim(),
    category: String(safe.category || '').trim(),
    material: String(safe.material || '').trim(),
    care: String(safe.care || '').trim(),
    season: String(safe.season || '').trim(),
  };
}

function normalizeAiMarket(market) {
  const safe = market && typeof market === 'object' ? market : {};
  return {
    currentMarketValue: String(safe.currentMarketValue || '').trim(),
    aftermarketValue: String(safe.aftermarketValue || '').trim(),
    conditionSummary: String(safe.conditionSummary || '').trim(),
    conditionRating: String(safe.conditionRating || '').trim(),
    resellRating: String(safe.resellRating || '').trim(),
    recommendedPlatform: String(safe.recommendedPlatform || '').trim(),
    platformRationale: String(safe.platformRationale || '').trim(),
    shoppingPoshProwessScore: String(safe.shoppingPoshProwessScore || '').trim(),
    shoppingPoshProwessValue: String(safe.shoppingPoshProwessValue || '').trim(),
    disclaimer: String(safe.disclaimer || '').trim(),
    updatedAt: String(safe.updatedAt || '').trim(),
    source: String(safe.source || '').trim(),
  };
}

function normalizeIntent(intent) {
  const safe = intent && typeof intent === 'object' ? intent : {};
  return { wear: !!safe.wear, sell: !!safe.sell };
}

function normalizeListing(listing) {
  const safe = listing && typeof listing === 'object' ? listing : {};
  const allowed = new Set(['none', 'draft', 'listed', 'sold']);
  return {
    status: allowed.has(safe.status) ? safe.status : 'none',
    platform: String(safe.platform || ''),
    listingUrl: String(safe.listingUrl || ''),
    price: String(safe.price || ''),
  };
}

function normalizeItem(item, idx) {
  item.id = item.id || uid();
  item.label = String(item.label || '').trim();
  item.tags = normalizeTags(item.tags);
  item.imageData = typeof item.imageData === 'string' ? item.imageData : '';
  item.backImageData = typeof item.backImageData === 'string' ? item.backImageData : '';
  item.tagImageData = typeof item.tagImageData === 'string' ? item.tagImageData : '';
  item.careImageData = typeof item.careImageData === 'string' ? item.careImageData : '';
  item.imageMediaId = String(item.imageMediaId || item.privateMediaId || '');
  item.backImageMediaId = String(item.backImageMediaId || '');
  item.tagImageMediaId = String(item.tagImageMediaId || item.tagPrivateMediaId || '');
  item.careImageMediaId = String(item.careImageMediaId || '');
  item.imageSha256 = String(item.imageSha256 || '');
  item.backImageSha256 = String(item.backImageSha256 || '');
  item.tagImageSha256 = String(item.tagImageSha256 || '');
  item.careImageSha256 = String(item.careImageSha256 || '');
  item.mediaStatus = String(item.mediaStatus || (item.imageMediaId ? 'private' : item.imageData ? 'local' : 'empty'));
  item.createdAt = String(item.createdAt || new Date().toISOString());
  item.updatedAt = String(item.updatedAt || item.createdAt);
  item.version = Math.max(1, parseInt(item.version || 1, 10) || 1);
  item.ocrText = typeof item.ocrText === 'string' ? item.ocrText : '';
  item.ocrUpdatedAt = typeof item.ocrUpdatedAt === 'string' ? item.ocrUpdatedAt : '';
  item.details = normalizeItemDetails(item.details);
  const legacySurroundingCategory = String(item.details.category || '').toLowerCase();
  const legacyBranch = legacySurroundingCategory.includes('book') ? 'library'
    : legacySurroundingCategory.includes('storage') ? 'storage'
      : legacySurroundingCategory.includes('plant') || legacySurroundingCategory.includes('garden') ? 'garden'
        : legacySurroundingCategory.includes('grocery') || legacySurroundingCategory.includes('pantry') ? 'pantry'
          : item.surrounding?.branch;
  item.surrounding = normalizeSurroundingDetails({
    ...(item.surrounding || {}),
    ...(legacyBranch ? { branch: legacyBranch } : {}),
    ...(!item.surrounding?.kind && item.details.category ? { kind: item.details.category } : {}),
  });
  item.aiMarket = normalizeAiMarket(item.aiMarket);
  item.intent = normalizeIntent(item.intent || DEFAULT_INTENT);
  item.listing = normalizeListing(item.listing || DEFAULT_LISTING);
  item.aiNeedsReview = !!item.aiNeedsReview;
  item.workwear = !!item.workwear;
  item.publicMirror = !!item.publicMirror;
  item.wearStatus = ['clean', 'worn', 'laundry'].includes(item.wearStatus) ? item.wearStatus : DEFAULT_WEAR_STATUS;
  item.pendingAiSuggestion = item.pendingAiSuggestion && typeof item.pendingAiSuggestion === 'object'
    ? item.pendingAiSuggestion
    : null;
  if (item.stack === undefined) item.stack = 0;
  if (item.position === undefined) item.position = idx;
  if (item.row === undefined) item.row = 0;
  if (item.cropY === undefined) item.cropY = 50;
  item.cropY = Math.max(0, Math.min(100, parseInt(item.cropY ?? 50, 10) || 0));
  return item;
}

function migrateMirror(mirror) {
  const safeMirror = {
    subjectImageData: mirror && typeof mirror.subjectImageData === 'string' ? mirror.subjectImageData : '',
    subjectMediaId: mirror && typeof mirror.subjectMediaId === 'string' ? mirror.subjectMediaId : '',
    views: mirror && mirror.views && typeof mirror.views === 'object' ? { ...mirror.views } : {},
    viewMediaIds: mirror && mirror.viewMediaIds && typeof mirror.viewMediaIds === 'object' ? { ...mirror.viewMediaIds } : {},
    poseSlots: {},
    activeView: mirror && typeof mirror.activeView === 'string' ? mirror.activeView : 'front',
    activePoseId: mirror && typeof mirror.activePoseId === 'string' ? mirror.activePoseId : (mirror?.activeView || 'front'),
    outfits: Array.isArray(mirror && mirror.outfits) ? mirror.outfits : [],
    ootdHistory: Array.isArray(mirror && mirror.ootdHistory) ? mirror.ootdHistory : [],
  };

  if (safeMirror.subjectImageData && !safeMirror.views.front) {
    safeMirror.views.front = safeMirror.subjectImageData;
  }
  DEFAULT_VIEWS.forEach(view => {
    if (typeof safeMirror.views[view] !== 'string') safeMirror.views[view] = '';
    if (typeof safeMirror.viewMediaIds[view] !== 'string') safeMirror.viewMediaIds[view] = '';
    const sourceSlot = mirror?.poseSlots?.[view] || {};
    safeMirror.poseSlots[view] = {
      imageData: typeof sourceSlot.imageData === 'string' ? sourceSlot.imageData : safeMirror.views[view],
      mediaId: String(sourceSlot.mediaId || safeMirror.viewMediaIds[view] || ''),
      sha256: String(sourceSlot.sha256 || ''),
      capturedAt: String(sourceSlot.capturedAt || ''),
      syncStatus: String(sourceSlot.syncStatus || (sourceSlot.mediaId || safeMirror.viewMediaIds[view] ? 'private' : sourceSlot.imageData || safeMirror.views[view] ? 'local' : 'empty')),
    };
  });
  if (!safeMirror.views[safeMirror.activeView] && !safeMirror.viewMediaIds[safeMirror.activeView]) safeMirror.activeView = 'front';
  if (!DEFAULT_VIEWS.includes(safeMirror.activePoseId)) safeMirror.activePoseId = 'front';
  safeMirror.subjectImageData = safeMirror.views[safeMirror.activeView] || safeMirror.views.front || safeMirror.subjectImageData || '';
  safeMirror.subjectMediaId = safeMirror.poseSlots[safeMirror.activePoseId]?.mediaId || safeMirror.viewMediaIds[safeMirror.activeView] || safeMirror.viewMediaIds.front || safeMirror.subjectMediaId || '';

  safeMirror.outfits = safeMirror.outfits.map(outfit => ({
    id: outfit.id || uid(),
    name: outfit.name || 'Untitled Look',
    createdAt: outfit.createdAt || new Date().toISOString(),
    updatedAt: outfit.updatedAt || outfit.createdAt || new Date().toISOString(),
    snapshotImageData: outfit.snapshotImageData || '',
    snapshotMediaId: String(outfit.snapshotMediaId || outfit.resultMediaId || ''),
    resultMediaId: String(outfit.resultMediaId || outfit.snapshotMediaId || ''),
    subjectImageData: outfit.subjectImageData || safeMirror.subjectImageData || '',
    subjectMediaId: String(outfit.subjectMediaId || ''),
    poseId: DEFAULT_VIEWS.includes(outfit.poseId) ? outfit.poseId : 'front',
    itemIds: Array.isArray(outfit.itemIds) ? outfit.itemIds.map(String) : [],
    prompt: String(outfit.prompt || ''),
    aiCostUsd: Number(outfit.aiCostUsd || 0),
    model: String(outfit.model || ''),
    status: String(outfit.status || 'saved'),
    items: Array.isArray(outfit.items)
      ? outfit.items.map(item => ({
          sourceItemId: item.sourceItemId || '',
          label: item.label || '',
          imageData: item.imageData || '',
          imageMediaId: String(item.imageMediaId || ''),
          x: item.x ?? 0.5,
          y: item.y ?? 0.5,
          width: item.width ?? 0.24,
          height: item.height ?? 0.24,
          rotation: item.rotation ?? 0,
          z: item.z ?? 1,
        }))
      : [],
  }));

  safeMirror.ootdHistory = safeMirror.ootdHistory.filter(Boolean).map(entry => ({
    id: entry.id || uid(),
    name: String(entry.name || 'Today\'s fit'),
    createdAt: entry.createdAt || new Date().toISOString(),
    updatedAt: entry.updatedAt || entry.createdAt || new Date().toISOString(),
    itemIds: Array.isArray(entry.itemIds) ? entry.itemIds.map(String) : [],
    note: String(entry.note || ''),
    photoData: typeof entry.photoData === 'string' ? entry.photoData : '',
    photoMediaId: String(entry.photoMediaId || ''),
    photoSha256: String(entry.photoSha256 || ''),
  }));

  return safeMirror;
}

function normalizeCalendarEvent(event = {}) {
  const safe = event && typeof event === 'object' ? event : {};
  return {
    ...DEFAULT_CALENDAR_EVENT,
    ...safe,
    id: String(safe.id || uid()),
    title: String(safe.title || 'Untitled event').trim(),
    date: String(safe.date || '').trim(),
    type: ['event', 'outfit', 'care', 'reminder'].includes(safe.type) ? safe.type : 'event',
    repeat: ['none', 'weekly', 'monthly'].includes(safe.repeat) ? safe.repeat : 'none',
    note: String(safe.note || '').trim(),
    itemIds: Array.isArray(safe.itemIds) ? [...new Set(safe.itemIds.map(String))] : [],
    createdAt: safe.createdAt || new Date().toISOString(),
    updatedAt: safe.updatedAt || safe.createdAt || new Date().toISOString(),
  };
}

function normalizeWitnessEntry(entry = {}) {
  const safe = entry && typeof entry === 'object' ? entry : {};
  return {
    ...DEFAULT_WITNESS_ENTRY,
    id: String(safe.id || uid()),
    note: String(safe.note || '').trim().slice(0, 2_000),
    reflection: String(safe.reflection || '').trim().slice(0, 600),
    summary: String(safe.summary || '').trim().slice(0, 280),
    kind: ['haiku', 'quote', 'compression'].includes(safe.kind) ? safe.kind : 'compression',
    weather: String(safe.weather || '').trim().slice(0, 120),
    createdAt: safe.createdAt || new Date().toISOString(),
  };
}

function normalizeSurroundingBoardPost(post = {}) {
  const safe = post && typeof post === 'object' ? post : {};
  const branches = ['garden', 'library', 'storage', 'pantry', 'care', 'objects'];
  return {
    ...DEFAULT_SURROUNDING_BOARD_POST,
    id: String(safe.id || uid()),
    branch: branches.includes(safe.branch) ? safe.branch : 'objects',
    body: String(safe.body || '').trim().slice(0, 600),
    prompt: String(safe.prompt || '').trim().slice(0, 80),
    linkedItemId: String(safe.linkedItemId || ''),
    destination: 'self',
    createdAt: safe.createdAt || new Date().toISOString(),
    updatedAt: safe.updatedAt || safe.createdAt || new Date().toISOString(),
  };
}

function migrateState(state) {
  const safeState = state || clone(EMPTY_STATE);
  const fromVersion = Number.isFinite(safeState.schemaVersion) ? safeState.schemaVersion : (safeState.collections ? 2 : 1);
  if (fromVersion < CURRENT_SCHEMA_VERSION) {
    lastMigrationInfo = { migrated: true, from: fromVersion, to: CURRENT_SCHEMA_VERSION };
  }
  safeState.schemaVersion = CURRENT_SCHEMA_VERSION;
  if (!safeState.locations) safeState.locations = [];
  safeState.mirror = migrateMirror(safeState.mirror);
  safeState.calendar = Array.isArray(safeState.calendar) ? safeState.calendar.filter(Boolean).map(normalizeCalendarEvent) : [];
  safeState.witness = Array.isArray(safeState.witness) ? safeState.witness.filter(Boolean).map(normalizeWitnessEntry).slice(0, 60) : [];
  safeState.surroundingBoards = Array.isArray(safeState.surroundingBoards)
    ? safeState.surroundingBoards.filter(Boolean).map(normalizeSurroundingBoardPost).filter(post => post.body).slice(0, 240)
    : [];
  safeState.activity = migrateActivity(safeState.activity);
  safeState.preferences = migratePreferences(safeState.preferences);

  if (!Array.isArray(safeState.collections) || !safeState.collections.length) {
    safeState.collections = [{ ...DEFAULT_COLLECTION, createdAt: new Date().toISOString() }];
  }
  safeState.collections = safeState.collections.map(collection => ({
    id: collection.id || uid(),
    name: collection.name || 'My Space',
    vibe: collection.vibe || 'closet',
    createdAt: collection.createdAt || new Date().toISOString(),
    updatedAt: collection.updatedAt || collection.createdAt || new Date().toISOString(),
    version: Math.max(1, parseInt(collection.version || 1, 10) || 1),
  }));
  if (!safeState.activeCollectionId || !safeState.collections.find(c => c.id === safeState.activeCollectionId)) {
    safeState.activeCollectionId = safeState.collections[0].id;
  }

  for (const loc of safeState.locations) {
    if (!loc.collectionId || !safeState.collections.find(c => c.id === loc.collectionId)) {
      loc.collectionId = safeState.collections[0].id;
    }
    if (loc.imageData === undefined) loc.imageData = '';
    loc.imageMediaId = String(loc.imageMediaId || '');
    loc.imageSha256 = String(loc.imageSha256 || '');
    if (loc.layoutImageData === undefined) loc.layoutImageData = '';
    loc.layoutImageMediaId = String(loc.layoutImageMediaId || '');
    loc.layoutImageSha256 = String(loc.layoutImageSha256 || '');
    loc.layoutUpdatedAt = String(loc.layoutUpdatedAt || '');
    loc.createdAt = String(loc.createdAt || new Date().toISOString());
    loc.updatedAt = String(loc.updatedAt || loc.createdAt);
    loc.version = Math.max(1, parseInt(loc.version || 1, 10) || 1);
    if (!['home', 'sell', 'mixed'].includes(loc.purpose)) {
      loc.purpose = /sell|resale|listing/i.test(loc.name || '') ? 'sell' : 'home';
    }
    if (!Array.isArray(loc.zones)) loc.zones = [];

    for (const zone of loc.zones) {
      if (zone.imageData === undefined) zone.imageData = '';
      zone.imageMediaId = String(zone.imageMediaId || '');
      zone.imageSha256 = String(zone.imageSha256 || '');
      zone.rowMediaIds = Array.isArray(zone.rowMediaIds) ? zone.rowMediaIds.map(value => String(value || '')) : [];
      zone.rowSha256 = Array.isArray(zone.rowSha256) ? zone.rowSha256.map(value => String(value || '')) : [];
      zone.createdAt = String(zone.createdAt || new Date().toISOString());
      zone.updatedAt = String(zone.updatedAt || zone.createdAt);
      zone.version = Math.max(1, parseInt(zone.version || 1, 10) || 1);
      zone.layout = normalizeLayout(zone.layout);
      normalizeRowImages(zone);
      if (!Array.isArray(zone.items)) zone.items = [];

      zone.items.forEach((item, idx) => normalizeItem(item, idx));

      normalizeZonePositions(zone);
    }
  }

  return safeState;
}

function migratePreferences(preferences) {
  const safePrefs = preferences && typeof preferences === 'object' ? preferences : {};
  return {
    weatherSource: ['geolocation', 'zip'].includes(safePrefs.weatherSource) ? safePrefs.weatherSource : '',
    weatherZip: String(safePrefs.weatherZip || ''),
    defaultToSurroundings: !!safePrefs.defaultToSurroundings,
    defaultSurroundingBranch: ['all', 'garden', 'library', 'storage', 'pantry', 'care', 'objects'].includes(safePrefs.defaultSurroundingBranch) ? safePrefs.defaultSurroundingBranch : 'library',
    surroundingsInterface: ['simple', 'advanced'].includes(safePrefs.surroundingsInterface) ? safePrefs.surroundingsInterface : 'simple',
    surroundingAiAssist: !!safePrefs.surroundingAiAssist,
    sockPairsAvailable: Math.max(0, Math.min(200, Number.isFinite(Number(safePrefs.sockPairsAvailable)) ? Math.round(Number(safePrefs.sockPairsAvailable)) : 12)),
    browserNotifications: !!safePrefs.browserNotifications,
  };
}

function migrateActivity(activity) {
  const safeActivity = activity && typeof activity === 'object' ? activity : {};
  return {
    lastAdded: migrateActivityEntry(safeActivity.lastAdded),
    lastMoved: migrateActivityEntry(safeActivity.lastMoved),
    lastRemoved: migrateActivityEntry(safeActivity.lastRemoved),
    auditLog: Array.isArray(safeActivity.auditLog)
      ? safeActivity.auditLog.map(migrateAuditEntry).filter(Boolean).slice(0, 200)
      : [],
  };
}

function migrateActivityEntry(entry) {
  if (!entry || typeof entry !== 'object') return null;
  return {
    label: entry.label || '',
    at: entry.at || new Date().toISOString(),
    type: entry.type || '',
  };
}

function migrateAuditEntry(entry) {
  if (!entry || typeof entry !== 'object') return null;
  return {
    id: entry.id || uid(),
    action: entry.action || 'changed',
    label: entry.label || '',
    type: entry.type || '',
    at: entry.at || new Date().toISOString(),
    imageData: typeof entry.imageData === 'string' ? entry.imageData : '',
    locationName: entry.locationName || '',
    zoneName: entry.zoneName || '',
    itemName: entry.itemName || '',
    collectionId: entry.collectionId || '',
    locationId: entry.locationId || '',
    zoneId: entry.zoneId || '',
    itemId: entry.itemId || '',
  };
}

function setActivity(state, key, label, type, detail = {}) {
  state.activity = migrateActivity(state.activity);
  const entry = {
    id: uid(),
    label: label || '',
    type: type || '',
    action: detail.action || key.replace(/^last/, '').toLowerCase() || 'changed',
    at: new Date().toISOString(),
    imageData: detail.imageData || '',
    locationName: detail.locationName || '',
    zoneName: detail.zoneName || '',
    itemName: detail.itemName || '',
    collectionId: detail.collectionId || '',
    locationId: detail.locationId || '',
    zoneId: detail.zoneId || '',
    itemId: detail.itemId || '',
  };
  state.activity[key] = entry;
  state.activity.auditLog = [entry, ...(state.activity.auditLog || [])].slice(0, 200);
  emitAuditEntry(entry);
}

function uid() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  if (globalThis.crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    return [...bytes].map(byte => byte.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now().toString(36)}-${performance.now().toString(36).replace('.', '')}`;
}

function itemName(item) {
  return item && item.label ? item.label : 'Unlabeled item';
}

const Store = (() => {
  let persistToken = 0;

  async function init() {
    if (!initPromise) {
      initPromise = (async () => {
        const userId = authenticatedUserId();
        const key = profileStateKey(userId);
        let indexedState = await readIndexedState(key);
        if (!indexedState && !userId) {
          const oldIndexedState = await readIndexedState(LEGACY_DB_STATE_KEY);
          if (oldIndexedState) {
            indexedState = oldIndexedState;
            await writeIndexedState(oldIndexedState, GUEST_DB_STATE_KEY).catch(() => {});
          }
        }
        if (indexedState) {
          memoryState = migrateState(indexedState);
          return memoryState;
        }

        const legacyState = loadLegacyState(userId);
        memoryState = legacyState || migrateState(clone(EMPTY_STATE));

        if (legacyState) {
          try {
            await writeIndexedState(memoryState);
            localStorage.removeItem(legacyFallbackKey(userId));
          } catch {
            // Keep the migrated legacy state in memory and let future saves fall back gracefully.
          }
        }

        return memoryState;
      })();
    }

    return initPromise;
  }

  function load() {
    return clone(memoryState);
  }

  function save(state) {
    undoSnapshot = clone(memoryState);
    undoLabel = 'Undo last local change';
    memoryState = migrateState(clone(state));
    const snapshot = clone(memoryState);
    const token = ++persistToken;
    emitStateChange(snapshot);

    void writeIndexedState(snapshot)
      .then(() => {
        if (token !== persistToken) return;
        try {
          localStorage.removeItem(legacyFallbackKey());
        } catch {
          // Ignore legacy cleanup failures after IndexedDB succeeds.
        }
      })
      .catch(error => {
        try {
          localStorage.setItem(legacyFallbackKey(), JSON.stringify(snapshot));
        } catch (fallbackError) {
          emitPersistError(fallbackError || error);
        }
      });
  }

  function undoLastChange() {
    if (!undoSnapshot) return false;
    const current = clone(memoryState);
    memoryState = migrateState(clone(undoSnapshot));
    undoSnapshot = current;
    undoLabel = 'Redo last local change';
    void writeIndexedState(memoryState).catch(error => emitPersistError(error));
    const entry = {
      id: uid(),
      label: 'Undid last local change',
      type: 'system',
      action: 'updated',
      at: new Date().toISOString(),
      imageData: '',
      locationName: '',
      zoneName: '',
      itemName: '',
      collectionId: '',
      locationId: '',
      zoneId: '',
      itemId: '',
    };
    emitAuditEntry(entry);
    return true;
  }

  function canUndo() {
    return !!undoSnapshot;
  }

  function getUndoLabel() {
    return undoLabel || 'Undo Last Change';
  }

  function getLocations() {
    return memoryState.locations.filter(loc => loc.collectionId === memoryState.activeCollectionId);
  }

  function getAllLocationsUnfiltered() {
    return memoryState.locations;
  }

  function getCollections() {
    return memoryState.collections || [];
  }

  function getActiveCollectionId() {
    return memoryState.activeCollectionId;
  }

  function setActiveCollection(id) {
    const state = load();
    if (state.collections.find(c => c.id === id)) {
      state.activeCollectionId = id;
      save(state);
    }
  }

  function addCollection(name, vibe) {
    const state = load();
    const collection = {
      id: uid(),
      name: name || 'New Space',
      vibe: vibe || 'closet',
      createdAt: new Date().toISOString(),
    };
    state.collections.push(collection);
    state.activeCollectionId = collection.id;
    setActivity(state, 'lastAdded', `Space added: ${collection.name}`, 'collection', {
      action: 'added',
      locationName: collection.name,
      collectionId: collection.id,
    });
    save(state);
    return collection;
  }

  function updateCollection(id, patch) {
    const state = load();
    const collection = state.collections.find(c => c.id === id);
    if (collection) {
      if (patch.name !== undefined) collection.name = patch.name;
      if (patch.vibe !== undefined) collection.vibe = patch.vibe;
      save(state);
    }
  }

  function deleteCollection(id) {
    const state = load();
    if (state.collections.length <= 1) return false;
    const collection = state.collections.find(c => c.id === id);
    state.collections = state.collections.filter(c => c.id !== id);
    state.locations = state.locations.filter(loc => loc.collectionId !== id);
    if (state.activeCollectionId === id) state.activeCollectionId = state.collections[0].id;
    if (collection) setActivity(state, 'lastRemoved', `Space removed: ${collection.name}`, 'collection', {
      action: 'deleted',
      locationName: collection.name,
      collectionId: collection.id,
    });
    save(state);
    return true;
  }

  function getLocation(id) {
    return memoryState.locations.find(location => location.id === id) || null;
  }

  function getState() {
    return load();
  }

  function getMirror() {
    return memoryState.mirror;
  }

  function addLocation(name, type, collectionId = '') {
    const state = load();
    const loc = {
      id: uid(),
      name,
      type,
      imageData: '',
      imageMediaId: '',
      imageSha256: '',
      layoutImageData: '',
      layoutImageMediaId: '',
      layoutImageSha256: '',
      layoutUpdatedAt: '',
      zones: [],
      collectionId: collectionId || state.activeCollectionId || (state.collections[0] && state.collections[0].id),
    };
    state.locations.push(loc);
    setActivity(state, 'lastAdded', `Location added: ${loc.name}`, 'location', {
      action: 'added',
      locationName: loc.name,
      locationId: loc.id,
    });
    save(state);
    return loc;
  }

  function updateLocationName(id, name) {
    const state = load();
    const loc = state.locations.find(location => location.id === id);
    if (loc) {
      const previous = loc.name;
      loc.name = name;
      setActivity(state, 'lastMoved', `Location renamed: ${previous} to ${loc.name}`, 'location', {
        action: 'updated',
        locationName: loc.name,
        locationId: loc.id,
      });
      save(state);
    }
  }

  function updateLocationImage(id, imageData) {
    const state = load();
    const loc = state.locations.find(location => location.id === id);
    if (loc) {
      loc.imageData = imageData || '';
      setActivity(state, 'lastMoved', `Location photo saved: ${loc.name}`, 'location', {
        action: 'updated',
        imageData: loc.imageData,
        locationName: loc.name,
        locationId: loc.id,
      });
      save(state);
    }
  }

  function updateLocationLayout(id, { imageData = '', mediaId = '', sha256 = '' } = {}) {
    const state = load();
    const loc = state.locations.find(location => location.id === id);
    if (!loc) return;
    loc.layoutImageData = imageData || '';
    loc.layoutImageMediaId = String(mediaId || '');
    loc.layoutImageSha256 = String(sha256 || '');
    loc.layoutUpdatedAt = new Date().toISOString();
    setActivity(state, 'lastMoved', `Room layout saved: ${loc.name}`, 'location', {
      action: 'updated',
      imageData: loc.layoutImageData,
      locationName: loc.name,
      locationId: loc.id,
    });
    save(state);
  }

  function updateLocationPurpose(id, purpose) {
    const state = load();
    const loc = state.locations.find(location => location.id === id);
    if (!loc) return;
    loc.purpose = ['home', 'sell', 'mixed'].includes(purpose) ? purpose : 'home';
    setActivity(state, 'lastMoved', `Closet purpose saved: ${loc.name} / ${loc.purpose}`, 'location', {
      action: 'updated',
      locationName: loc.name,
      locationId: loc.id,
    });
    save(state);
  }

  function updateLocationFocus(id, focus = {}) {
    const state = load();
    const loc = state.locations.find(location => location.id === id);
    if (!loc) return;
    loc.focusTheme = String(focus.focusTheme || '').trim();
    loc.focusBranch = String(focus.focusBranch || '').trim();
    loc.focusSlogan = String(focus.focusSlogan || '').trim();
    loc.focusColor = String(focus.focusColor || '').trim();
    loc.updatedAt = new Date().toISOString();
    save(state);
  }

  function updatePreferences(patch) {
    const state = load();
    state.preferences = migratePreferences({ ...state.preferences, ...patch });
    save(state);
  }

  function saveCalendarEvent(event = {}) {
    const state = load();
    const nextEvent = normalizeCalendarEvent({ ...event, updatedAt: new Date().toISOString() });
    const index = state.calendar.findIndex(candidate => candidate.id === nextEvent.id);
    if (index === -1) state.calendar.unshift(nextEvent);
    else state.calendar[index] = nextEvent;
    save(state);
    return nextEvent;
  }

  function deleteCalendarEvent(eventId) {
    const state = load();
    state.calendar = state.calendar.filter(event => event.id !== String(eventId));
    save(state);
  }

  function saveWitnessEntry(entry = {}) {
    const state = load();
    const nextEntry = normalizeWitnessEntry(entry);
    state.witness = [nextEntry, ...(state.witness || []).filter(candidate => candidate.id !== nextEntry.id)].slice(0, 60);
    save(state);
    return nextEntry;
  }

  function deleteWitnessEntry(entryId) {
    const state = load();
    state.witness = (state.witness || []).filter(entry => entry.id !== String(entryId));
    save(state);
  }

  function saveSurroundingBoardPost(post = {}) {
    const state = load();
    const nextPost = normalizeSurroundingBoardPost({ ...post, updatedAt: new Date().toISOString() });
    if (!nextPost.body) return null;
    state.surroundingBoards = [
      nextPost,
      ...(state.surroundingBoards || []).filter(candidate => candidate.id !== nextPost.id),
    ].slice(0, 240);
    save(state);
    return nextPost;
  }

  function deleteSurroundingBoardPost(postId) {
    const state = load();
    state.surroundingBoards = (state.surroundingBoards || []).filter(post => post.id !== String(postId));
    save(state);
  }

  function deleteLocation(id) {
    const state = load();
    const location = state.locations.find(entry => entry.id === id);
    state.locations = state.locations.filter(entry => entry.id !== id);
    if (location) setActivity(state, 'lastRemoved', `Location removed: ${location.name}`, 'location', {
      action: 'deleted',
      imageData: location.imageData || '',
      locationName: location.name,
      locationId: location.id,
    });
    save(state);
  }

  function addZone(locationId, name) {
    const state = load();
    const loc = state.locations.find(location => location.id === locationId);
    if (!loc) return null;

    const zone = {
      id: uid(),
      name,
      patternSeed: uid(),
      imageData: '',
      rowImages: [],
      layout: { ...DEFAULT_LAYOUT },
      items: [],
    };
    loc.zones.push(zone);
    setActivity(state, 'lastAdded', `Sub-location added: ${loc.name} / ${zone.name}`, 'zone', {
      action: 'added',
      locationName: loc.name,
      zoneName: zone.name,
      locationId: loc.id,
      zoneId: zone.id,
    });
    save(state);
    return zone;
  }

  function updateZoneName(locationId, zoneId, name) {
    const state = load();
    const zone = findZone(state, locationId, zoneId);
    if (zone) {
      const loc = state.locations.find(location => location.id === locationId);
      const previous = zone.name;
      zone.name = name;
      setActivity(state, 'lastMoved', `Sub-location renamed: ${previous} to ${zone.name}`, 'zone', {
        action: 'updated',
        locationName: loc ? loc.name : '',
        zoneName: zone.name,
        locationId,
        zoneId,
      });
      save(state);
    }
  }

  function updateZoneImage(locationId, zoneId, imageData) {
    const state = load();
    const zone = findZone(state, locationId, zoneId);
    if (zone) {
      const loc = state.locations.find(location => location.id === locationId);
      zone.imageData = imageData || '';
      setActivity(state, 'lastMoved', `Zone photo saved: ${zone.name}`, 'zone', {
        action: 'updated',
        imageData: zone.imageData,
        locationName: loc ? loc.name : '',
        zoneName: zone.name,
        locationId,
        zoneId,
      });
      save(state);
    }
  }

  function updateZoneRowImage(locationId, zoneId, row, imageData) {
    const state = load();
    const zone = findZone(state, locationId, zoneId);
    if (zone) {
      normalizeRowImages(zone);
      const loc = state.locations.find(location => location.id === locationId);
      zone.rowImages[row] = imageData || '';
      setActivity(state, 'lastMoved', `Row photo ${imageData ? 'saved' : 'cleared'}: ${zone.name}`, 'zone', {
        action: imageData ? 'updated' : 'deleted',
        imageData: imageData || '',
        locationName: loc ? loc.name : '',
        zoneName: zone.name,
        locationId,
        zoneId,
      });
      save(state);
    }
  }

  function updateZoneLayout(locationId, zoneId, layoutPatch) {
    const state = load();
    const zone = findZone(state, locationId, zoneId);
    if (zone) {
      zone.layout = normalizeLayout({ ...zone.layout, ...layoutPatch });
      normalizeRowImages(zone);
      zone.items.forEach(item => {
        item.stack = Math.max(0, Math.min(item.stack ?? 0, zone.layout.columns - 1));
        item.row = Math.max(0, Math.min(item.row ?? 0, zone.layout.maxRows - 1));
      });
      normalizeZonePositions(zone);
      save(state);
    }
  }

  function regenerateZonePattern(locationId, zoneId) {
    const state = load();
    const zone = findZone(state, locationId, zoneId);
    if (zone) {
      zone.patternSeed = uid();
      save(state);
      return zone.patternSeed;
    }
    return null;
  }

  function deleteZone(locationId, zoneId) {
    const state = load();
    const loc = state.locations.find(location => location.id === locationId);
    if (loc) {
      const zone = loc.zones.find(entry => entry.id === zoneId);
      loc.zones = loc.zones.filter(entry => entry.id !== zoneId);
      if (zone) setActivity(state, 'lastRemoved', `Sub-location removed: ${loc.name} / ${zone.name}`, 'zone', {
        action: 'deleted',
        imageData: zone.imageData || '',
        locationName: loc.name,
        zoneName: zone.name,
        locationId: loc.id,
        zoneId: zone.id,
      });
      save(state);
    }
  }

  function addItem(locationId, zoneId, imageData, placement = {}) {
    const state = load();
    const zone = findZone(state, locationId, zoneId);
    if (!zone) return null;

    const stack = placement.stack ?? 0;
    const row = placement.row ?? 0;
    const position = placement.position ?? nextTopPosition(zone, stack, row);

    const item = {
      id: uid(),
      label: '',
      tags: [],
      imageData,
      backImageData: '',
      backImageMediaId: '',
      backImageSha256: '',
      stack,
      position,
      row,
      cropY: placement.cropY ?? 50,
      tagImageData: '',
      workwear: false,
      publicMirror: false,
      wearStatus: DEFAULT_WEAR_STATUS,
      ocrText: '',
      ocrUpdatedAt: '',
      details: { ...DEFAULT_ITEM_DETAILS },
      aiMarket: { ...DEFAULT_AI_MARKET },
    };

    zone.items.push(item);
    normalizeZonePositions(zone);
    const location = state.locations.find(entry => entry.id === locationId);
    setActivity(state, 'lastAdded', `Article added: ${itemName(item)} in ${zone.name}`, 'item', {
      action: 'added',
      imageData: item.imageData,
      locationName: location ? location.name : '',
      zoneName: zone.name,
      itemName: itemName(item),
      locationId,
      zoneId,
      itemId: item.id,
    });
    save(state);
    return item;
  }

  function updateItem(locationId, zoneId, itemId, fields) {
    const state = load();
    const zone = findZone(state, locationId, zoneId);
    if (!zone) return;
    const item = zone.items.find(entry => entry.id === itemId);
    if (item) {
      Object.assign(item, fields);
      item.details = normalizeItemDetails(item.details);
      item.surrounding = normalizeSurroundingDetails(item.surrounding);
      item.aiMarket = normalizeAiMarket(item.aiMarket);
      item.intent = normalizeIntent(item.intent);
      item.listing = normalizeListing(item.listing);
      item.workwear = !!item.workwear;
      item.publicMirror = !!item.publicMirror;
      item.wearStatus = ['clean', 'worn', 'laundry'].includes(item.wearStatus) ? item.wearStatus : DEFAULT_WEAR_STATUS;
      item.tags = normalizeTags(item.tags);
      item.cropY = Math.max(0, Math.min(100, parseInt(item.cropY ?? 50, 10) || 0));
      const location = state.locations.find(entry => entry.id === locationId);
      setActivity(state, 'lastMoved', `Article saved: ${itemName(item)}`, 'item', {
        action: 'updated',
        imageData: item.imageData || item.tagImageData || '',
        locationName: location ? location.name : '',
        zoneName: zone.name,
        itemName: itemName(item),
        locationId,
        zoneId,
        itemId: item.id,
      });
      save(state);
    }
  }

  function moveItem(locationId, zoneId, itemId, targetStack, targetRow, targetPosition) {
    const state = load();
    const zone = findZone(state, locationId, zoneId);
    const location = state.locations.find(entry => entry.id === locationId);
    if (!zone) return;
    const item = zone.items.find(entry => entry.id === itemId);
    if (!item) return;

    const sourceStack = item.stack ?? 0;
    const sourceRow = item.row ?? 0;
    const sameGroup = sourceStack === targetStack && sourceRow === targetRow;
    const targetGroup = zone.items
      .filter(entry => entry.id !== itemId && (entry.stack ?? 0) === targetStack && (entry.row ?? 0) === targetRow)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

    const adjustedTargetPosition = sameGroup && targetPosition > (item.position ?? 0)
      ? targetPosition - 1
      : targetPosition;
    const clampedPosition = Math.max(0, Math.min(adjustedTargetPosition, targetGroup.length));

    item.stack = targetStack;
    item.row = targetRow;
    targetGroup.splice(clampedPosition, 0, item);
    targetGroup.forEach((entry, idx) => {
      entry.position = idx;
    });

    if (!sameGroup) {
      zone.items
        .filter(entry => entry.id !== itemId && (entry.stack ?? 0) === sourceStack && (entry.row ?? 0) === sourceRow)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .forEach((entry, idx) => {
          entry.position = idx;
        });
    }

    setActivity(
      state,
      'lastMoved',
      `Article moved: ${itemName(item)} in ${(location && location.name) || 'Location'} / ${zone.name}`,
      'item',
      {
        action: 'moved',
        imageData: item.imageData,
        locationName: (location && location.name) || '',
        zoneName: zone.name,
        itemName: itemName(item),
        locationId,
        zoneId,
        itemId: item.id,
      }
    );
    save(state);
  }

  function reassignItemToZone(fromLocationId, fromZoneId, toLocationId, toZoneId, itemId) {
    const state = load();
    const fromZone = findZone(state, fromLocationId, fromZoneId);
    const toZone = findZone(state, toLocationId, toZoneId);
    if (!fromZone || !toZone) return;

    const itemIdx = fromZone.items.findIndex(entry => entry.id === itemId);
    if (itemIdx === -1) return;

    const [item] = fromZone.items.splice(itemIdx, 1);
    normalizeZonePositions(fromZone);

    item.stack = 0;
    item.row = 0;
    item.position = nextTopPosition(toZone, 0, 0);
    toZone.items.push(item);
    normalizeZonePositions(toZone);
    const fromLocation = state.locations.find(entry => entry.id === fromLocationId);
    const toLocation = state.locations.find(entry => entry.id === toLocationId);
    setActivity(
      state,
      'lastMoved',
      `Article moved: ${itemName(item)} from ${fromLocation ? fromLocation.name : 'Location'} / ${fromZone.name} to ${toLocation ? toLocation.name : 'Location'} / ${toZone.name}`,
      'item',
      {
        action: 'moved',
        imageData: item.imageData,
        locationName: toLocation ? toLocation.name : '',
        zoneName: toZone.name,
        itemName: itemName(item),
        locationId: toLocationId,
        zoneId: toZoneId,
        itemId: item.id,
      }
    );
    save(state);
  }

  function deleteItem(locationId, zoneId, itemId, reason = 'removed') {
    const state = load();
    const zone = findZone(state, locationId, zoneId);
    if (zone) {
      const item = zone.items.find(entry => entry.id === itemId);
      zone.items = zone.items.filter(entry => entry.id !== itemId);
      normalizeZonePositions(zone);
      const location = state.locations.find(entry => entry.id === locationId);
      const safeReason = ['sold', 'donated', 'thrown out', 'removed'].includes(reason) ? reason : 'removed';
      if (item) setActivity(state, 'lastRemoved', `${itemName(item)} marked ${safeReason} and removed from ${zone.name}`, 'item', {
        action: 'deleted',
        reason: safeReason,
        imageData: item.imageData,
        locationName: location ? location.name : '',
        zoneName: zone.name,
        itemName: itemName(item),
        locationId,
        zoneId,
        itemId: item.id,
      });
      save(state);
    }
  }

  function setItemsWearStatus(itemIds, wearStatus) {
    const state = load();
    const ids = new Set((itemIds || []).map(String));
    const nextStatus = ['clean', 'worn', 'laundry'].includes(wearStatus) ? wearStatus : DEFAULT_WEAR_STATUS;
    let updated = 0;
    for (const location of state.locations || []) {
      for (const zone of location.zones || []) {
        for (const item of zone.items || []) {
          if (!ids.has(String(item.id))) continue;
          item.wearStatus = nextStatus;
          item.updatedAt = new Date().toISOString();
          if (nextStatus === 'worn') item.lastWornAt = new Date().toISOString();
          updated += 1;
        }
      }
    }
    if (updated) setActivity(state, 'lastMoved', `${updated} item${updated === 1 ? '' : 's'} marked ${nextStatus}`, 'item', { action: 'wear-status', wearStatus: nextStatus, itemIds: [...ids] });
    save(state);
    return updated;
  }

  function setMirrorSubjectImage(imageData) {
    const state = load();
    state.mirror.subjectImageData = imageData || '';
    const view = state.mirror.activeView || 'front';
    if (!state.mirror.views) state.mirror.views = {};
    state.mirror.views[view] = imageData || '';
    save(state);
  }

  function setMirrorView(view, imageData) {
    const state = load();
    if (!state.mirror.views) state.mirror.views = {};
    if (!state.mirror.viewMediaIds) state.mirror.viewMediaIds = {};
    state.mirror.views[view] = imageData || '';
    if (imageData) state.mirror.viewMediaIds[view] = '';
    if (view === state.mirror.activeView) state.mirror.subjectImageData = imageData || '';
    save(state);
  }

  function setMirrorPose(poseId, patch = {}) {
    if (!DEFAULT_VIEWS.includes(poseId)) return null;
    const state = load();
    if (!state.mirror.poseSlots) state.mirror.poseSlots = clone(DEFAULT_POSE_SLOTS);
    const current = state.mirror.poseSlots[poseId] || { ...DEFAULT_POSE_SLOT };
    const next = {
      ...current,
      ...patch,
      imageData: patch.imageData !== undefined ? String(patch.imageData || '') : current.imageData || '',
      mediaId: patch.mediaId !== undefined ? String(patch.mediaId || '') : current.mediaId || '',
      sha256: patch.sha256 !== undefined ? String(patch.sha256 || '') : current.sha256 || '',
      capturedAt: patch.capturedAt || current.capturedAt || new Date().toISOString(),
    };
    if (patch.imageData) {
      next.mediaId = '';
      next.sha256 = '';
      next.syncStatus = 'local';
    }
    state.mirror.poseSlots[poseId] = next;
    state.mirror.views[poseId] = next.imageData;
    state.mirror.viewMediaIds[poseId] = next.mediaId;
    state.mirror.activePoseId = poseId;
    state.mirror.activeView = poseId;
    state.mirror.subjectImageData = next.imageData;
    state.mirror.subjectMediaId = next.mediaId;
    save(state);
    return next;
  }

  function setActiveMirrorView(view) {
    const state = load();
    state.mirror.activeView = view || 'front';
    state.mirror.subjectImageData = (state.mirror.views && state.mirror.views[state.mirror.activeView]) || '';
    save(state);
  }

  function saveMirrorOutfit(outfit) {
    const state = load();
    const nextOutfit = {
      id: outfit.id || uid(),
      name: outfit.name || 'Untitled Look',
      createdAt: outfit.createdAt || new Date().toISOString(),
      snapshotImageData: outfit.snapshotImageData || '',
      snapshotMediaId: outfit.snapshotMediaId || outfit.resultMediaId || '',
      resultMediaId: outfit.resultMediaId || outfit.snapshotMediaId || '',
      subjectImageData: outfit.subjectImageData || state.mirror.subjectImageData || '',
      subjectMediaId: outfit.subjectMediaId || state.mirror.subjectMediaId || '',
      poseId: outfit.poseId || state.mirror.activePoseId || 'front',
      itemIds: Array.isArray(outfit.itemIds) ? [...outfit.itemIds] : [],
      prompt: outfit.prompt || '',
      aiCostUsd: Number(outfit.aiCostUsd || 0),
      model: outfit.model || '',
      status: outfit.status || 'saved',
      updatedAt: new Date().toISOString(),
      items: Array.isArray(outfit.items) ? outfit.items.map(item => ({ ...item })) : [],
    };

    const idx = state.mirror.outfits.findIndex(entry => entry.id === nextOutfit.id);
    if (idx === -1) state.mirror.outfits.unshift(nextOutfit);
    else state.mirror.outfits[idx] = nextOutfit;
    save(state);
    return nextOutfit;
  }

  function deleteMirrorOutfit(outfitId) {
    const state = load();
    state.mirror.outfits = state.mirror.outfits.filter(outfit => outfit.id !== outfitId);
    save(state);
  }

  function saveOotdHistory(entry = {}) {
    const state = load();
    state.mirror.ootdHistory ||= [];
    const nextEntry = {
      id: entry.id || uid(),
      name: entry.name || 'Today\'s fit',
      createdAt: entry.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      itemIds: Array.isArray(entry.itemIds) ? [...new Set(entry.itemIds.map(String))] : [],
      note: entry.note || '',
      photoData: entry.photoData || '',
      photoMediaId: entry.photoMediaId || '',
      photoSha256: entry.photoSha256 || '',
    };
    const index = state.mirror.ootdHistory.findIndex(history => history.id === nextEntry.id);
    if (index === -1) state.mirror.ootdHistory.unshift(nextEntry);
    else state.mirror.ootdHistory[index] = nextEntry;
    save(state);
    return nextEntry;
  }

  function replaceState(nextState) {
    const safeState = migrateState(clone(nextState || EMPTY_STATE));
    save(safeState);
    return safeState;
  }

  function findZone(state, locationId, zoneId) {
    const loc = state.locations.find(location => location.id === locationId);
    return loc ? loc.zones.find(zone => zone.id === zoneId) : null;
  }

  function consumeMigrationInfo() {
    const info = { ...lastMigrationInfo };
    lastMigrationInfo = { migrated: false, from: null, to: CURRENT_SCHEMA_VERSION };
    return info;
  }

  async function hasProfileState(userId) {
    if (!Number.isInteger(Number(userId)) || Number(userId) <= 0) return false;
    return !!(await readIndexedState(profileStateKey(Number(userId))));
  }

  async function claimCurrentStateForUser(userId, { clearGuest = true } = {}) {
    if (!Number.isInteger(Number(userId)) || Number(userId) <= 0) throw new Error('A valid profile id is required.');
    const snapshot = migrateState(clone(memoryState));
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    await writeIndexedState(snapshot, profileStateKey(Number(userId)));
    await writeIndexedState(snapshot, `state:pre-profile-backup:${stamp}`);
    if (clearGuest) await writeIndexedState(migrateState(clone(EMPTY_STATE)), GUEST_DB_STATE_KEY);
    return true;
  }

  async function hasGuestState() {
    const guest = await readIndexedState(GUEST_DB_STATE_KEY);
    if (!guest) return false;
    const safe = migrateState(clone(guest));
    return !!(safe.locations?.length || safe.mirror?.outfits?.length || safe.mirror?.subjectImageData);
  }

  async function claimGuestStateForUser(userId) {
    if (!Number.isInteger(Number(userId)) || Number(userId) <= 0) throw new Error('A valid profile id is required.');
    const guest = await readIndexedState(GUEST_DB_STATE_KEY);
    if (!guest) return false;
    const snapshot = migrateState(clone(guest));
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    await writeIndexedState(snapshot, profileStateKey(Number(userId)));
    await writeIndexedState(snapshot, `state:pre-profile-backup:${stamp}`);
    await writeIndexedState(migrateState(clone(EMPTY_STATE)), GUEST_DB_STATE_KEY);
    return true;
  }

  async function sealProfileState(snapshot, userId = authenticatedUserId()) {
    if (!Number.isInteger(Number(userId)) || Number(userId) <= 0) return false;
    const safe = migrateState(clone(snapshot || memoryState));
    await writeIndexedState(safe, profileStateKey(Number(userId)));
    await deleteIndexedStatesMatching(key => key === LEGACY_DB_STATE_KEY || key.startsWith('state:pre-profile-backup:'));
    try {
      Object.keys(localStorage)
        .filter(key => key === STORAGE_KEY || key.startsWith(`${STORAGE_KEY}:user:`))
        .forEach(key => localStorage.removeItem(key));
    } catch {
      // IndexedDB remains the authoritative compact state.
    }
    return true;
  }

  return {
    init,
    hasProfileState,
    claimCurrentStateForUser,
    hasGuestState,
    claimGuestStateForUser,
    sealProfileState,
    consumeMigrationInfo,
    undoLastChange,
    canUndo,
    getUndoLabel,
    getLocations,
    getAllLocationsUnfiltered,
    getCollections,
    getActiveCollectionId,
    setActiveCollection,
    addCollection,
    updateCollection,
    deleteCollection,
    getLocation,
    getState,
    getMirror,
    replaceState,
    addLocation,
    updateLocationName,
    updateLocationImage,
    updateLocationLayout,
    updateLocationPurpose,
    updateLocationFocus,
    updatePreferences,
    saveCalendarEvent,
    deleteCalendarEvent,
    saveWitnessEntry,
    deleteWitnessEntry,
    saveSurroundingBoardPost,
    deleteSurroundingBoardPost,
    deleteLocation,
    addZone,
    updateZoneName,
    updateZoneImage,
    updateZoneRowImage,
    updateZoneLayout,
    regenerateZonePattern,
    deleteZone,
    addItem,
    updateItem,
    moveItem,
    reassignItemToZone,
    deleteItem,
    setItemsWearStatus,
    setMirrorSubjectImage,
    setMirrorView,
    setMirrorPose,
    setActiveMirrorView,
    saveMirrorOutfit,
    deleteMirrorOutfit,
    saveOotdHistory,
  };
})();

export { Store };
